import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Sparkles, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const QUIZ_QUESTIONS = [
  {
    id: 1,
    question: "Where are you in your self-improvement journey?",
    options: [
      { id: 'a', text: "Just getting started", points: { guide: 2, subscription: 1 } },
      { id: 'b', text: "Tried some things, inconsistent results", points: { guide: 2, subscription: 2 } },
      { id: 'c', text: "Have a routine but want to level up", points: { guide: 1, subscription: 2 } },
    ]
  },
  {
    id: 2,
    question: "What's your biggest challenge right now?",
    options: [
      { id: 'a', text: "Don't know where to start", points: { guide: 2, subscription: 1 } },
      { id: 'b', text: "Staying consistent with routines", points: { guide: 1, subscription: 3 } },
      { id: 'c', text: "Need personalized guidance", points: { guide: 0, subscription: 3 } },
    ]
  },
  {
    id: 3,
    question: "How do you prefer to learn?",
    options: [
      { id: 'a', text: "Self-paced, on my own time", points: { guide: 3, subscription: 1 } },
      { id: 'b', text: "With accountability and feedback", points: { guide: 1, subscription: 3 } },
      { id: 'c', text: "Mix of both", points: { guide: 2, subscription: 2 } },
    ]
  },
  {
    id: 4,
    question: "What's your timeline for seeing results?",
    options: [
      { id: 'a', text: "I'm patient, building long-term habits", points: { guide: 2, subscription: 1 } },
      { id: 'b', text: "Want to see progress within weeks", points: { guide: 1, subscription: 2 } },
      { id: 'c', text: "Need structure to stay on track", points: { guide: 1, subscription: 3 } },
    ]
  }
];

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
};

export const Quiz = ({ onClose }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [recommendation, setRecommendation] = useState(null);

  const handleAnswer = (option) => {
    const newAnswers = [...answers, option];
    setAnswers(newAnswers);

    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate recommendation
      let guidePoints = 0;
      let subscriptionPoints = 0;
      
      newAnswers.forEach(answer => {
        guidePoints += answer.points.guide;
        subscriptionPoints += answer.points.subscription;
      });

      setRecommendation(subscriptionPoints > guidePoints ? 'subscription' : 'guide');
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1));
    }
  };

  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3 }}
        className="bg-black w-full max-w-lg p-8 md:p-10 relative"
        onClick={e => e.stopPropagation()}
        data-testid="quiz-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#999999] hover:text-white text-2xl leading-none"
        >
          ×
        </button>

        {!showResult ? (
          <>
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between text-xs text-[#999999] mb-2">
                <span>Question {currentQuestion + 1} of {QUIZ_QUESTIONS.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-1 bg-[#E5E0D6] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#D4AF37]"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestion}
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-8">
                  {QUIZ_QUESTIONS[currentQuestion].question}
                </h3>

                <div className="space-y-3">
                  {QUIZ_QUESTIONS[currentQuestion].options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleAnswer(option)}
                      className="w-full text-left p-4 border border-[#1a1a1a] bg-[#0f0f0f] hover:border-[#D4AF37] hover:bg-[#0f0f0f] transition-all duration-200 group"
                      data-testid={`quiz-option-${option.id}`}
                    >
                      <span className="text-white group-hover:text-white">
                        {option.text}
                      </span>
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Back button */}
            {currentQuestion > 0 && (
              <button
                onClick={handleBack}
                className="mt-6 text-sm text-[#999999] hover:text-white flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
          </>
        ) : (
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="text-center"
          >
            <div className="w-16 h-16 bg-[#0f0f0f] rounded-full flex items-center justify-center mx-auto mb-6">
              {recommendation === 'subscription' ? (
                <Users className="w-8 h-8 text-[#D4AF37]" />
              ) : (
                <Sparkles className="w-8 h-8 text-[#D4AF37]" />
              )}
            </div>

            <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
              Your Recommendation
            </p>

            <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
              {recommendation === 'subscription' 
                ? 'Signal Accountability' 
                : 'The Signal Guide'}
            </h3>

            <p className="text-[#999999] mb-8">
              {recommendation === 'subscription'
                ? "Based on your answers, you'd benefit most from accountability and personalized guidance. The subscription gives you weekly check-ins and direct support."
                : "Based on your answers, you're ready to take action on your own. The complete guide gives you everything you need to transform at your own pace."}
            </p>

            <div className="space-y-3">
              <Link 
                to={`/products/${recommendation === 'subscription' ? 'glow-up-subscription' : 'rares-method-guide'}`}
                onClick={onClose}
              >
                <Button 
                  className="w-full btn-premium bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none py-4 text-sm uppercase tracking-widest"
                  data-testid="quiz-result-cta"
                >
                  View {recommendation === 'subscription' ? 'Subscription' : 'Guide'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/products" onClick={onClose}>
                <Button 
                  variant="outline"
                  className="w-full border-[#1a1a1a] text-[#999999] hover:bg-[#0f0f0f] rounded-none py-4 text-sm"
                >
                  View All Products
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Quiz;
